import type { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { LOG_LEVEL, LoggerService, type LogLevel } from '@core/services/logger.service.js';
import { type ProblemDetails } from '@mmscan/shared-types';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, throwError } from 'rxjs';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const messageService = inject(MessageService);
  const logger = inject(LoggerService);
  const dialogService = inject(DialogService);

  const closeAllDialogs = () => {
    for (const ref of dialogService.dialogComponentRefMap.keys()) {
      ref.close();
    }
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unknown error occurred';
      let errorLevel: LogLevel = LOG_LEVEL.ERROR;
      let shouldShowToast = true;

      if (error.error instanceof ErrorEvent) {
        errorMessage = `Client Error: ${error.error.message}`;
      } else {
        switch (error.status) {
          case 400:
            errorMessage = 'Bad Request';
            errorLevel = LOG_LEVEL.WARN;
            break;
          case 401:
            // 401 handling (retry + redirect) is owned by httpAuthInterceptor.
            // By the time a 401 reaches here the auth interceptor has already
            // attempted a token-refresh retry, so there is nothing left to do.
            errorLevel = LOG_LEVEL.ERROR;
            shouldShowToast = false;
            closeAllDialogs();
            break;
          case 403:
            errorMessage = "Forbidden - You don't have permission";
            closeAllDialogs();
            router.navigate(['/forbidden']);
            errorLevel = LOG_LEVEL.WARN;
            break;
          case 404:
            errorMessage = 'Resource not found';
            shouldShowToast = false; // Handled by component itself
            errorLevel = LOG_LEVEL.WARN;
            break;
          case 409:
            // Server returns RFC 9457 Problem Details for conflicts — prefer `detail`
            errorMessage = (error.error as ProblemDetails)?.detail ?? 'Conflict';
            errorLevel = LOG_LEVEL.WARN;
            break;
          case 500:
            errorMessage = 'Internal Server Error';
            // TODO: Consider a dedicated 500 error page if this becomes a common occurrence, as it may indicate server instability. For now, we'll just log it and show a toast.
            // router.navigate(['/server-error']);
            errorLevel = LOG_LEVEL.ERROR;
            closeAllDialogs();
            break;
          default:
            errorMessage = (error.error as ProblemDetails)?.detail ?? `Unexpected Error (Status: ${error.status})`;
            errorLevel = LOG_LEVEL.ERROR;
            break;
        }
      }

      if (shouldShowToast) {
        let severity = 'error';
        switch (errorLevel) {
          case LOG_LEVEL.ERROR:
            severity = 'error';
            break;
          case LOG_LEVEL.WARN:
            severity = 'warn';
        }

        messageService.add({
          severity: severity,
          summary: errorMessage,
        });
      }

      logger.error('HTTP Error:', error);

      return throwError(() => error);
    })
  );
};
